import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

interface ReportCardProps {
  label?: string;
  action?: ReactNode;
  children: ReactNode;
}

export function ReportCard({ label, action, children }: ReportCardProps): ReactNode {
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.divider}`,
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: 0,
      }}
    >
      {(label !== undefined || action !== undefined) && (
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
        >
          {label !== undefined && <SectionLabel>{label}</SectionLabel>}
          {action}
        </Box>
      )}
      {children}
    </Box>
  );
}

export function SectionLabel({ children }: { children: ReactNode }): ReactNode {
  return (
    <Typography
      component="span"
      sx={{
        fontSize: 9,
        fontWeight: 700,
        textTransform: 'uppercase',
        color: 'text.disabled',
      }}
    >
      {children}
    </Typography>
  );
}

interface SectionHeadingProps {
  title: string;
  description?: string;
}

export function SectionHeading({ title, description }: SectionHeadingProps): ReactNode {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography component="h2" sx={{ fontSize: 18, fontWeight: 600, color: 'text.primary' }}>
        {title}
      </Typography>
      {description !== undefined && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{description}</Typography>
      )}
    </Box>
  );
}

export function ReportSection({
  title,
  description,
  action,
  children,
}: SectionHeadingProps & { action?: ReactNode; children: ReactNode }): ReactNode {
  const heading = (
    <SectionHeading title={title} {...(description === undefined ? {} : { description })} />
  );
  return (
    <Box
      data-test={`report-section-${title.toLowerCase().replaceAll(' ', '-')}`}
      sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}
    >
      {action === undefined ? (
        heading
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {heading}
          {action}
        </Box>
      )}
      {children}
    </Box>
  );
}
